import * as React from 'react';

import { JobStageData } from 'shared/models/job-stages';

import { timeRelativeDay } from '../common/timestamp';
import { User } from '../sfc/user';
import { Job, JobProgressCounts, Search, SearchStatus } from '../state';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';
import { JobSearchesTitle } from './job-searches-title';

type Column =
    | 'title'
    | 'added'
    | 'emailed'
    | 'responded'
    | 'notQualified'
    | 'sentToClient'
    | 'accepted'
    | 'offer'
    | 'hired'
    | 'created'
    | 'owner'
    | 'createdBy';

interface ProgressRow {
    createdAt?: number;
    createdBy?: string;
    id: string;
    name: string;
    search?: Search;
    sourcedBy: 'ai_sourced' | 'recruiter_sourced';
    userId?: string;
}

interface JobSearchesProgressProps {
    searches: Search[];
    progressCounts: JobProgressCounts[];
    displayPercent: 'count' | 'percent' | 'both';
    onDisplaySearchInfo: (searchId: string) => void;
    onCloneSearch: (data: Search) => void;
    onUpdateSearch: (id: string, updates: Partial<Search>, refetchSearches: boolean) => void;
    onDeleteSearch: (data: Search) => void;
    jobStages: JobStageData[];
    job: Job;
}

export const JobSearchesProgress: React.FC<JobSearchesProgressProps> = ({
    searches,
    progressCounts,
    displayPercent,
    jobStages,
    job,
    onCloneSearch,
    onUpdateSearch,
    onDeleteSearch
}) => {
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const table = tableContainerRef.current;
        if (table) {
            table.scrollTo({
                behavior: 'smooth',
                top: table.scrollHeight
            });
        }
    }, [searches, progressCounts, displayPercent]);

    const columns: Array<DataPanelColumn<Column>> = [
        { id: 'title', label: 'Title' },
        { id: 'added', label: 'Added' },
        { id: 'emailed', label: 'Emailed' },
        { id: 'responded', label: 'Responded' },
        { id: 'notQualified', label: 'Not Qualified' },
        { id: 'sentToClient', label: 'Sent to Client' },
        { id: 'accepted', label: 'Accepted' },
        { id: 'offer', label: 'Offer' },
        { id: 'hired', label: 'Hired' },
        { id: 'created', label: 'Created' },
        { id: 'owner', label: 'Owner' },
        { id: 'createdBy', label: 'Created By' }
    ];

    const buildRowData = React.useMemo((): ProgressRow[] => {
        let rowData: ProgressRow[] = searches
            .filter((s) => s.status !== SearchStatus.Initial)
            .map((s) => ({
                createdAt: s.createdAt,
                createdBy: s.createdBy,
                id: s.id,
                name: s.name,
                search: s,
                sourcedBy: 'ai_sourced' as const,
                userId: s.userId
            }));

        if (progressCounts.find((p) => p.searchId === null && p.sourcedBy === 'recruiter_sourced')) {
            rowData = rowData.concat([{ name: 'Manually Sourced', id: null, sourcedBy: 'recruiter_sourced' }]);
        }
        if (progressCounts.find((p) => p.searchId === null && p.sourcedBy === 'ai_sourced')) {
            rowData = rowData.concat([{ name: 'Old AI searches', id: null, sourcedBy: 'ai_sourced' }]);
        }

        return rowData;
    }, [searches, progressCounts]);

    const getStageCount = (searchId: string, sourcedBy: 'ai_sourced' | 'recruiter_sourced', stage: string): number => {
        const searchCounts = progressCounts.find((p) => p.searchId === searchId && p.sourcedBy === sourcedBy);
        if (!searchCounts) return 0;
        let count = 0;
        const stageStage = jobStages.find((s) => s.name === stage);

        for (const c of searchCounts.counts) {
            const cStage = jobStages.find((s) => s.name === c.stage);
            if (stage === c.stage || (stageStage && cStage && stageStage.id <= cStage.id)) {
                count += c.count;
            }
        }
        return count;
    };

    const formatProgressCount = (count: number, totalCount: number, hasPercentOf: boolean): string | JSX.Element => {
        if (displayPercent === 'count' || !hasPercentOf) {
            return count.toString();
        }

        if (totalCount === 0 || count === 0) {
            return count.toString();
        }

        const percent = Math.ceil((count * 100) / totalCount);

        if (displayPercent === 'percent') {
            return <span>{percent}%</span>;
        }

        return (
            <span>
                {count} <span className="secondary">({percent}%)</span>
            </span>
        );
    };

    const getFormattedCount = (row: ProgressRow, stage: string, percentOfStage?: string): string | JSX.Element => {
        const count = getStageCount(row.id, row.sourcedBy, stage);
        const totalCount = percentOfStage ? getStageCount(row.id, row.sourcedBy, percentOfStage) : 0;
        const result = formatProgressCount(count, totalCount, !!percentOfStage);
        return result;
    };

    const getCellValue =
        (column: Column) =>
        (row: ProgressRow): CellValue => {
            switch (column) {
                case 'title':
                    return row.name;
                case 'added':
                    return getStageCount(row.id, row.sourcedBy, jobStages[0].name);
                case 'emailed':
                    return getStageCount(row.id, row.sourcedBy, 'outreach_sent');
                case 'responded':
                    return getStageCount(row.id, row.sourcedBy, 'response_received');
                case 'notQualified':
                    return getStageCount(row.id, row.sourcedBy, 'not_qualified_on_review');
                case 'sentToClient':
                    return getStageCount(row.id, row.sourcedBy, 'awaiting_client_feedback');
                case 'accepted':
                    return getStageCount(row.id, row.sourcedBy, 'client_first_round');
                case 'offer':
                    return getStageCount(row.id, row.sourcedBy, 'offer');
                case 'hired':
                    return getStageCount(row.id, row.sourcedBy, 'hired');
                case 'created':
                    return row.createdAt ?? '-';
                case 'owner':
                    return row.userId ?? '-';
                case 'createdBy':
                    return row.createdBy ?? '-';
            }
        };

    const getCellRenderer = {
        accepted: (_1: CellValue, row: ProgressRow) =>
            getFormattedCount(row, 'client_first_round', 'awaiting_client_feedback'),
        added: (_1: CellValue, row: ProgressRow) => getFormattedCount(row, jobStages[0].name),
        created: (value: CellValue) => (value !== '-' ? timeRelativeDay(value as number) : '-'),
        createdBy: (value: CellValue) => (value !== '-' ? <User id={value as string} /> : '-'),
        emailed: (_1: CellValue, row: ProgressRow) => getFormattedCount(row, 'outreach_sent'),
        hired: (_1: CellValue, row: ProgressRow) => getFormattedCount(row, 'hired', 'offer'),
        notQualified: (_1: CellValue, row: ProgressRow) =>
            getFormattedCount(row, 'not_qualified_on_review', 'response_received'),
        offer: (_1: CellValue, row: ProgressRow) => getFormattedCount(row, 'offer', 'client_first_round'),
        owner: (value: CellValue) => (value !== '-' ? <User id={value as string} /> : '-'),
        responded: (_1: CellValue, row: ProgressRow) => getFormattedCount(row, 'response_received', 'outreach_sent'),
        sentToClient: (_1: CellValue, row: ProgressRow) =>
            getFormattedCount(row, 'awaiting_client_feedback', 'response_received'),
        title: (_: CellValue, row: ProgressRow) => (
            <JobSearchesTitle
                onCloneSearch={onCloneSearch}
                onUpdateSearch={onUpdateSearch}
                onDeleteSearch={onDeleteSearch}
                search={row.search}
                job={job}
                name={row.name}
            />
        )
    };

    return (
        <DataPanel
            columns={columns}
            data={buildRowData}
            getCellValue={getCellValue}
            getCellRenderer={getCellRenderer}
            hidePagination={true}
            className="job-searches-data-panel"
            pageSize={100}
            tableContainerRef={tableContainerRef}
            sortableColumns={['created']}
            initialSortColumn="created"
            initialSortDirection="asc"
        />
    );
};
